// Schema-free helpers of the email content editors (Weekly Income, Email
// Marketing campaigns). Same rules as the backend
// (server_elakay/elakay_services/emailContent/*): nothing here knows a
// template's keys, every function walks the document it is given, so new
// keys, nested objects and arrays are preserved.
//
// Paths are arrays of keys / indexes in the editors (["hero", "items", 0]),
// and dotted strings in reports ("hero.items[0]").

export const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
export const clone = (value) => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)));
export const typeOf = (value) => (value === null ? "null" : Array.isArray(value) ? "array" : typeof value);
const joinPath = (prefix, key) => (typeof key === "number" ? `${prefix}[${key}]` : prefix ? `${prefix}.${key}` : String(key));
export const pathToString = (parts) => parts.reduce((acc, key) => joinPath(acc, key), "");
// Token syntax of a path: "items.0.title".
export const pathToToken = (parts) => parts.map(String).join(".");

export function getIn(document, parts) {
  let node = document;
  for (const part of parts) {
    if (node === null || typeof node !== "object" || !Object.prototype.hasOwnProperty.call(node, part)) return undefined;
    node = node[part];
  }
  return node;
}
export const getPath = (document, dotted) => getIn(document, String(dotted || "").replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean));

// Immutable update: copies only the containers on the path.
export function setIn(document, parts, value) {
  if (!parts.length) return value;
  const [head, ...rest] = parts;
  const container = Array.isArray(document) ? [...document] : { ...(isPlainObject(document) ? document : {}) };
  container[head] = setIn(container[head], rest, value);
  return container;
}
export function removeIn(document, parts) {
  if (parts.length === 1) {
    if (Array.isArray(document)) return document.filter((_, index) => index !== parts[0]);
    const next = { ...document };
    delete next[parts[0]];
    return next;
  }
  const [head, ...rest] = parts;
  return setIn(document, [head], removeIn(document[head], rest));
}
export function renameKeyIn(document, parts, newKey) {
  const parent = getIn(document, parts.slice(0, -1));
  const oldKey = parts[parts.length - 1];
  const entries = Object.entries(parent).map(([key, value]) => (key === oldKey ? [newKey, value] : [key, value]));
  return setIn(document, parts.slice(0, -1), Object.fromEntries(entries));
}

export function listPaths(value) {
  const out = [];
  const walk = (node, path) => {
    if (path) out.push({ path, type: typeOf(node), value: isPlainObject(node) || Array.isArray(node) ? undefined : node });
    if (isPlainObject(node)) Object.entries(node).forEach(([key, child]) => walk(child, joinPath(path, key)));
    else if (Array.isArray(node)) node.forEach((child, index) => walk(child, joinPath(path, index)));
  };
  walk(value, "");
  return out;
}
// A key usable inside a {{token}} segment (letters, digits, _ and -). A key
// with a dot, a space... stays editable but cannot be written in a token.
const TOKEN_SEGMENT = /^[A-Za-z0-9_-]+$/;
export const isTokenizableKey = (key) => typeof key === "number" || TOKEN_SEGMENT.test(String(key));
// ["content", "items", 0, "title"] -> "content.items.0.title" (null when a
// key cannot be written in a token, or the path starts with an index).
export const partsToToken = (parts) => (parts.length && typeof parts[0] === "string" && /^[A-Za-z_]/.test(parts[0]) && parts.every(isTokenizableKey) ? parts.map(String).join(".") : null);
// "content.items[0].title"; a key a token cannot express is quoted: ["a.b"].
export const displayPath = (parts) => parts.reduce((acc, key) => (typeof key === "number" ? `${acc}[${key}]` : !TOKEN_SEGMENT.test(key) ? `${acc}[${JSON.stringify(key)}]` : acc ? `${acc}.${key}` : key), "");
// Every scalar leaf: { parts, path (display), token (or null) }.
export function listLeaves(document) {
  const out = [];
  const walk = (node, parts) => {
    if (isPlainObject(node)) Object.entries(node).forEach(([key, child]) => walk(child, [...parts, key]));
    else if (Array.isArray(node)) node.forEach((child, index) => walk(child, [...parts, index]));
    else if (parts.length) out.push({ parts, path: displayPath(parts), token: partsToToken(parts), value: node });
  };
  walk(document, []);
  return out;
}
// Scalar paths in token syntax ("items.0.title").
export const scalarTokenPaths = (document) => listLeaves(document).map((leaf) => leaf.token).filter(Boolean);

export function compareStructures(source, target) {
  const missing = [];
  const extra = [];
  const typeMismatches = [];
  const scalar = (t) => !["object", "array"].includes(t);
  const walk = (a, b, path) => {
    if (isPlainObject(a) && isPlainObject(b)) {
      Object.keys(a).forEach((key) => { const child = joinPath(path, key); if (!Object.prototype.hasOwnProperty.call(b, key)) missing.push(child); else walk(a[key], b[key], child); });
      Object.keys(b).forEach((key) => { if (!Object.prototype.hasOwnProperty.call(a, key)) extra.push(joinPath(path, key)); });
      return;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      a.forEach((item, index) => { if (index >= b.length) missing.push(joinPath(path, index)); else walk(item, b[index], joinPath(path, index)); });
      for (let index = a.length; index < b.length; index++) extra.push(joinPath(path, index));
      return;
    }
    const expected = typeOf(a);
    const actual = typeOf(b);
    if (expected !== actual && !(scalar(expected) && scalar(actual) && (expected === "null" || actual === "null"))) typeMismatches.push({ path, expected, actual });
  };
  walk(source, target, "");
  return { missing, extra, typeMismatches, inSync: !missing.length && !extra.length && !typeMismatches.length };
}

const URL_KEY = /(url|link|href|src)$/i;
const IMAGE_KEY = /(^|[_.])?(image|imageUrl|img|src|logo|photo|thumbnail|banner)$/i;
export const isUrlKey = (key) => URL_KEY.test(String(key));
export const isImageKey = (key) => IMAGE_KEY.test(String(key));
export const isUrlLike = (key, value) => typeof value === "string" && (isUrlKey(key) || isImageKey(key) || /^(https?:|mailto:)/i.test(value.trim())) && (!value || /^(https?:|mailto:|\/|#|\{\{)/i.test(value.trim()));
const isTokenOnly = (value) => typeof value === "string" && /^\s*(\{\{[^}]+\}\}\s*)+$/.test(value);

function missingValue(key, value, fill) {
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, missingValue(k, v, fill)]));
  if (Array.isArray(value)) return value.map((item) => missingValue(key, item, fill));
  if (typeof value !== "string") return clone(value);
  if (isUrlLike(key, value) || isTokenOnly(value) || !value.trim()) return value;
  return fill === "source" ? value : "";
}

// Adds the keys of `source` missing from `target` (texts empty, or the
// source text as an explicit fallback); never removes or overwrites.
export function syncMissingKeys(source, target, { fill = "empty" } = {}) {
  const added = [];
  const walk = (a, b, path, key) => {
    if (isPlainObject(a)) {
      const result = isPlainObject(b) ? clone(b) : {};
      Object.entries(a).forEach(([childKey, childValue]) => {
        const childPath = joinPath(path, childKey);
        if (!Object.prototype.hasOwnProperty.call(result, childKey)) { result[childKey] = missingValue(childKey, childValue, fill); added.push(childPath); }
        else result[childKey] = walk(childValue, result[childKey], childPath, childKey);
      });
      return result;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      const result = clone(b);
      a.forEach((item, index) => {
        if (index >= result.length) { result.push(missingValue(key, item, fill)); added.push(joinPath(path, index)); }
        else result[index] = walk(item, result[index], joinPath(path, index), key);
      });
      return result;
    }
    return b;
  };
  return { document: walk(isPlainObject(source) ? source : {}, isPlainObject(target) ? target : {}, "", ""), added };
}

export function removeExtraKeys(source, target) {
  const removed = [];
  const walk = (a, b, path) => {
    if (isPlainObject(a) && isPlainObject(b)) {
      const result = {};
      Object.entries(b).forEach(([key, value]) => { if (Object.prototype.hasOwnProperty.call(a, key)) result[key] = walk(a[key], value, joinPath(path, key)); else removed.push(joinPath(path, key)); });
      return result;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      for (let index = a.length; index < b.length; index++) removed.push(joinPath(path, index));
      return b.slice(0, a.length).map((item, index) => walk(a[index], item, joinPath(path, index)));
    }
    return clone(b);
  };
  return { document: walk(source, target, ""), removed };
}

// ── Tokens ──────────────────────────────────────────────────────────────
export const TOKEN_PATTERN = /\{\{\s*([#/]?[A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+)*)\s*\}\}/g;
export const TOKEN_CATEGORIES = ["translatable_content", "user_data", "system_data", "dynamic_html", "protected_system_token", "unknown"];

export function detectTokens(html) {
  const found = new Map();
  const source = String(html || "");
  for (const match of source.matchAll(TOKEN_PATTERN)) {
    const name = match[1];
    if (!found.has(name)) found.set(name, { name, count: 0, line: source.slice(0, match.index).split("\n").length });
    found.get(name).count += 1;
  }
  return [...found.values()];
}
export const tokensIn = (text) => detectTokens(text).map((token) => token.name);

// registry: { types, protected, contentPrefix, contentPaths } (closed set,
// e.g. Weekly Income), or { open: true, isReserved(name), reservedCategory(name) }:
// every non-reserved token is a content path (Email Marketing campaigns).
export function classifyToken(name, registry = {}) {
  if ((registry.protected || []).includes(name)) return "protected_system_token";
  if (registry.open) return registry.isReserved?.(name) ? registry.reservedCategory?.(name) || "system_data" : "translatable_content";
  if (registry.types && Object.prototype.hasOwnProperty.call(registry.types, name)) return registry.types[name];
  if (registry.contentPrefix && name.startsWith(registry.contentPrefix)) {
    const path = name.slice(registry.contentPrefix.length);
    return !registry.contentPaths || registry.contentPaths.includes(path) ? "translatable_content" : "unknown";
  }
  if ((registry.contentPaths || []).includes(name)) return "translatable_content";
  if (/^recipient\./.test(name)) return "user_data";
  if (/^(sender|platform|campaign)\./.test(name)) return "system_data";
  return "unknown";
}

export function inspectTokens(html, registry = {}) {
  const tokens = detectTokens(html).map((token) => ({ ...token, category: classifyToken(token.name, registry) }));
  const names = tokens.map((token) => token.name);
  return { tokens, count: tokens.length, missingProtected: (registry.protected || []).filter((name) => !names.includes(name)), unknown: tokens.filter((t) => t.category === "unknown").map((t) => t.name) };
}

export function diffTokens(beforeHtml, afterHtml, registry = {}) {
  const before = tokensIn(beforeHtml);
  const after = inspectTokens(afterHtml, registry);
  const names = after.tokens.map((token) => token.name);
  return { detected: after.count, added: names.filter((name) => !before.includes(name)), removed: before.filter((name) => !names.includes(name)), missingProtected: after.missingProtected, unknown: after.unknown, tokens: after.tokens };
}

// ── Validation ──────────────────────────────────────────────────────────
const VOID_TAGS = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "source", "track", "wbr"]);
const CHECKED_TAGS = new Set(["table", "tbody", "thead", "tfoot", "tr", "td", "th", "div", "span", "a", "p", "center", "strong", "b", "em", "i", "u", "ul", "ol", "h1", "h2", "h3", "h4", "h5", "h6", "html", "head", "body", "title", "style", "font"]);
const IMPLICIT_CLOSE = new Set(["p", "td", "th", "tr", "li", "tbody", "thead", "tfoot", "html", "head", "body"]);
const lineAt = (source, index) => source.slice(0, index).split("\n").length;

export function checkTagBalance(html) {
  const source = String(html || "").replace(/<!--[\s\S]*?-->/g, (comment) => comment.replace(/[^\n]/g, " "));
  const stack = [];
  const problems = [];
  for (const match of source.matchAll(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/?)>/g)) {
    const name = match[1].toLowerCase();
    if (!CHECKED_TAGS.has(name) || VOID_TAGS.has(name) || match[2] === "/") continue;
    const line = lineAt(source, match.index);
    if (!match[0].startsWith("</")) { stack.push({ name, line }); continue; }
    const index = stack.map((entry) => entry.name).lastIndexOf(name);
    if (index === -1) { problems.push({ code: "UNEXPECTED_END_TAG", message: `Unexpected </${name}> at line ${line} (no matching <${name}>)`, line }); continue; }
    stack.splice(index).slice(1).forEach((open) => { if (!IMPLICIT_CLOSE.has(open.name)) problems.push({ code: "UNCLOSED_TAG", message: `<${open.name}> opened at line ${open.line} is not closed before </${name}> at line ${line}`, line: open.line }); });
  }
  stack.forEach((open) => { if (!IMPLICIT_CLOSE.has(open.name)) problems.push({ code: "UNCLOSED_TAG", message: `<${open.name}> opened at line ${open.line} is never closed`, line: open.line }); });
  return problems;
}

// options: { registry, unknownTokens: "warn"|"block"|"ignore", checkStructure }
export function validateEmailHtml(html, options = {}) {
  const errors = [];
  const warnings = [];
  const source = typeof html === "string" ? html : "";
  if (!source.trim()) errors.push({ code: "EMPTY_HTML", message: "The template HTML is empty" });
  if (source.length > 200000) errors.push({ code: "HTML_TOO_LARGE", message: "The template HTML is too large (200 KB maximum)" });
  const script = source.search(/<script\b/i);
  if (script !== -1) errors.push({ code: "SCRIPT_NOT_ALLOWED", message: `<script> is not allowed in an email template (line ${lineAt(source, script)})`, line: lineAt(source, script) });
  const jsUrl = source.search(/javascript:/i);
  if (jsUrl !== -1) errors.push({ code: "SCRIPT_NOT_ALLOWED", message: `javascript: URLs are not allowed in an email template (line ${lineAt(source, jsUrl)})`, line: lineAt(source, jsUrl) });
  const handler = source.search(/<[^>]+\son[a-z]+\s*=/i);
  if (handler !== -1) errors.push({ code: "EVENT_HANDLER", message: `Event handler attributes (onclick, onload...) are not allowed (line ${lineAt(source, handler)})`, line: lineAt(source, handler) });
  const inspection = inspectTokens(source, options.registry || {});
  inspection.missingProtected.forEach((name) => errors.push({ code: "MISSING_PROTECTED_TOKEN", message: `Missing protected token: {{${name}}}`, token: name }));
  if (inspection.unknown.length && options.unknownTokens !== "ignore") {
    const entry = { code: "UNKNOWN_TOKEN", message: `Unknown token(s): ${inspection.unknown.map((name) => `{{${name}}}`).join(", ")}`, tokens: inspection.unknown };
    (options.unknownTokens === "block" ? errors : warnings).push(entry);
  }
  if (options.checkStructure !== false && source.trim()) checkTagBalance(source).forEach((problem) => warnings.push(problem));
  return { valid: !errors.length, errors, warnings, tokens: inspection.tokens, missingProtected: inspection.missingProtected };
}

// Offset of the first JSON syntax error (browsers no longer agree on the
// error message format): strict scan, only after JSON.parse failed.
export function locateJsonError(source) {
  let i = 0;
  const fail = () => { throw i; };
  const ws = () => { while (/[ \t\n\r]/.test(source[i] || "")) i++; };
  const literal = (word) => { if (source.startsWith(word, i)) i += word.length; else fail(); };
  const string = () => {
    i++;
    while (i < source.length) {
      const c = source[i];
      if (c === '"') { i++; return; }
      if (c === "\\") { i++; if (source[i] === "u") { if (!/^[0-9a-fA-F]{4}$/.test(source.slice(i + 1, i + 5))) fail(); i += 5; } else if ('"\\/bfnrt'.includes(source[i] || "#")) i++; else fail(); continue; }
      if (c < " ") fail();
      i++;
    }
    fail();
  };
  const number = () => { const m = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/.exec(source.slice(i)); if (!m) fail(); i += m[0].length; };
  const value = () => {
    ws();
    const c = source[i];
    if (c === "{") { i++; ws(); if (source[i] === "}") { i++; return; } for (;;) { ws(); if (source[i] !== '"') fail(); string(); ws(); if (source[i] !== ":") fail(); i++; value(); ws(); if (source[i] === ",") { i++; continue; } if (source[i] === "}") { i++; return; } fail(); } }
    if (c === "[") { i++; ws(); if (source[i] === "]") { i++; return; } for (;;) { value(); ws(); if (source[i] === ",") { i++; continue; } if (source[i] === "]") { i++; return; } fail(); } }
    if (c === '"') return string();
    if (c === "t") return literal("true");
    if (c === "f") return literal("false");
    if (c === "n") return literal("null");
    return number();
  };
  try { value(); ws(); if (i < source.length) fail(); return null; } catch (position) { return typeof position === "number" ? position : null; }
}

export function parseJsonWithLocation(text) {
  const source = String(text ?? "");
  try {
    return { ok: true, value: JSON.parse(source) };
  } catch (error) {
    const position = locateJsonError(source);
    if (position === null) return { ok: false, error: { code: "INVALID_JSON", message: `Invalid JSON: ${error.message.split("\n")[0]}`, line: null, column: null } };
    const before = source.slice(0, position).split("\n");
    const line = before.length;
    const column = before[before.length - 1].length + 1;
    const reason = position >= source.length ? "unexpected end of input" : `unexpected ${JSON.stringify(source[position])}`;
    return { ok: false, error: { code: "INVALID_JSON", message: `Invalid JSON at line ${line}, column ${column}: ${reason}`, line, column } };
  }
}

export function compareTranslation(source, target) {
  const structure = compareStructures(source, target);
  const warnings = [];
  listPaths(source).forEach(({ path, type, value }) => {
    if (["object", "array"].includes(type)) return;
    const translated = getPath(target, path);
    if (translated === undefined) return;
    const key = path.split(/[.[\]]/).filter(Boolean).pop();
    if (typeof value === "string") {
      const missing = tokensIn(value).filter((name) => !tokensIn(translated).includes(name));
      if (missing.length) warnings.push({ code: "TOKEN_MODIFIED", path, message: `${path}: token(s) ${missing.map((name) => `{{${name}}}`).join(", ")} missing or modified` });
      if (isUrlLike(key, value) && value.trim() && translated !== value) warnings.push({ code: "URL_MODIFIED", path, message: `${path}: URL changed (${value} → ${translated})` });
    } else if (["number", "boolean"].includes(type) && translated !== value) {
      warnings.push({ code: "VALUE_MODIFIED", path, message: `${path}: value changed (${value} → ${translated})` });
    }
  });
  return { ...structure, warnings };
}

// ── Form field inference ────────────────────────────────────────────────
// text | textarea | url | image | number | boolean | null | object | array
export function inferFieldType(key, value) {
  const type = typeOf(value);
  if (type === "object") return "object";
  if (type === "array") return "array";
  if (type === "boolean") return "boolean";
  if (type === "number") return "number";
  if (type === "null") return "null";
  const k = String(key);
  if (isImageKey(k)) return "image";
  if (isUrlKey(k) || /^(https?:|mailto:)/i.test(String(value).trim())) return "url";
  if (String(value).length > 80 || String(value).includes("\n")) return "textarea";
  return "text";
}
export const humanizeKey = (key) => (typeof key === "number" ? `#${key + 1}` : String(key).replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase()));
export const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
// Empty value with the same shape (new list item, new language).
export function blankLike(value) {
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, blankLike(v)]));
  if (Array.isArray(value)) return [];
  if (typeof value === "string") return "";
  if (typeof value === "number") return 0;
  if (typeof value === "boolean") return false;
  return null;
}
export const NEW_FIELD_VALUES = { text: "", textarea: "", url: "https://", image: "https://", number: 0, boolean: false, object: {}, array: [], null: null };

// ── Copy packages for AI / translation tools ───────────────────────────
const LANGUAGE_NAMES = { en: "English", fr: "French", es: "Spanish", ht: "Haitian Creole", pt: "Portuguese" };
export const languageName = (code) => LANGUAGE_NAMES[code] || code;

export function buildTranslationPackage({ document, sourceLanguage, targetLanguage, withPrompt = true, customInstructions = "" }) {
  const json = JSON.stringify(document, null, 2);
  if (!withPrompt) return json;
  return [
    `Translate the JSON below from ${languageName(sourceLanguage)} to ${targetLanguage ? languageName(targetLanguage) : "[TARGET LANGUAGE]"} for an Elakay email.`,
    "Rules:",
    "- Preserve every JSON key exactly (never translate, rename, add or remove a key).",
    "- Preserve the structure: same nesting, same arrays, same number of items, same value types.",
    "- Translate only human-readable text values.",
    "- Keep every {{token}} exactly as written (for example {{firstname}}, {{periodStart}}): never translate or remove it.",
    "- Do not translate keys. Keep URLs, email addresses, numbers, booleans, null values, IDs and variables unchanged.",
    "- Keep HTML entities and line breaks.",
    customInstructions ? `- ${customInstructions}` : null,
    "Return ONLY the translated JSON, valid, with no explanation and no Markdown fence.",
    "",
    json,
  ].filter((line) => line !== null).join("\n");
}

export function buildAiTemplatePrompt({ html, document, protectedTokens = [], tokens = [], tokenSyntax = "plain", emailType = "email", customInstructions = "" }) {
  const example = tokenSyntax === "content" ? "{{content.hero.title}}" : (document && Object.keys(document).length ? `{{${(listLeaves(document).find((leaf) => leaf.token) || { token: "hero.title" }).token}}}` : "{{hero.title}}");
  return [
    `You are editing the HTML email template of an Elakay ${emailType}.`,
    "Apply the changes I describe at the end. Constraints:",
    "- Email-safe HTML only: table-based layout, inline CSS, max-width around 640px, no <script>, no JavaScript, no external CSS, no forms, no event handlers (onclick...).",
    "- Keep every existing {{token}} exactly as written; each one is replaced by real data when the email is sent.",
    `- These protected tokens MUST stay in the template: ${protectedTokens.map((name) => `{{${name}}}`).join(", ") || "(none)"}.`,
    "- {{unsubscribeUrl}} must stay the href of a visible unsubscribe link.",
    "- Some tokens are HTML built at send time (for example {{incomeBlocks}}): keep them where a block of content can appear, never inside an attribute.",
    "- You MAY add new sections, texts, images (absolute https:// URLs) and links when useful.",
    `- If you add an editable text, image, link, label, button, section, or similar value to HTML, create a matching JSON key and use a {{full.path.to.key}} token in HTML (the full path from the JSON root, for example ${example}; array items by index: {{section.items.0.title}}).`,
    "- Do not assume any predefined JSON field names: any key, any nesting depth and arrays are allowed. Keys used in tokens may contain letters, digits, _ and -.",
    "- Keep the HTML and the JSON synchronized: every content token must exist in the JSON; keep JSON keys that the HTML does not use.",
    "- Return the full JSON content with your new keys added and every existing key preserved.",
    customInstructions ? `- ${customInstructions}` : null,
    "",
    "Answer with exactly two fenced blocks: first ```html (the complete template), then ```json (the complete content).",
    "",
    `Tokens currently used: ${tokens.map((name) => `{{${name}}}`).join(" ") || "(none)"}`,
    "",
    "Current HTML:",
    "```html",
    html || "",
    "```",
    "",
    "Current JSON content:",
    "```json",
    JSON.stringify(document || {}, null, 2),
    "```",
    "",
    "My changes:",
    "",
  ].filter((line) => line !== null).join("\n");
}

// HTML and/or JSON from an AI answer: fenced ```html / ```json blocks, or a
// bare HTML document / JSON object.
export function parseAiResponse(text) {
  const source = String(text || "");
  const fence = (lang) => { const m = new RegExp("```" + lang + "\\s*\\n([\\s\\S]*?)```", "i").exec(source); return m ? m[1].trim() : null; };
  let html = fence("html");
  let json = fence("json");
  if (html === null && json === null) {
    const trimmed = source.trim();
    if (/^[[{]/.test(trimmed)) json = trimmed;
    else if (/<[a-z!]/i.test(trimmed)) html = trimmed;
  }
  return { html, json };
}


// ── Path tree (Dynamic Content) ────────────────────────────────────────
// { key, parts, path, token, children[] } for a JSON document; leaves have
// no children. `query` keeps the branches whose path matches.
export function buildPathTree(document, query = "") {
  const q = String(query || "").trim().toLowerCase();
  const build = (node, parts) => {
    const entries = isPlainObject(node) ? Object.entries(node) : Array.isArray(node) ? node.map((child, index) => [index, child]) : [];
    return entries.map(([key, child]) => {
      const childParts = [...parts, key];
      const path = displayPath(childParts);
      const isLeaf = !isPlainObject(child) && !Array.isArray(child);
      const children = isLeaf ? [] : build(child, childParts);
      const matches = !q || path.toLowerCase().includes(q) || String(key).toLowerCase().includes(q);
      if (!matches && !children.length) return null;
      return { key, parts: childParts, path, token: isLeaf ? partsToToken(childParts) : null, isLeaf, children };
    }).filter(Boolean);
  };
  return build(document, []);
}

// ── Content tokens vs JSON ─────────────────────────────────────────────
// Value of a content token: the full JSON path; "content.<path>" also
// resolves <path> at the root (campaigns written with the earlier
// namespace convention). Same rule as the backend renderer.
export function resolveContentToken(document, name) {
  if (!isPlainObject(document)) return { found: false };
  const direct = getPath(document, name);
  if (direct !== undefined) return { found: true, value: direct };
  if (name.startsWith("content.")) {
    const legacy = getPath(document, name.slice("content.".length));
    if (legacy !== undefined) return { found: true, value: legacy };
  }
  return { found: false };
}

// HTML content tokens against a JSON document: resolved / missing, unused
// JSON leaves (reported, never deleted), keys a token cannot express.
// isContent(name): which tokens are content (default: not a known type).
export function analyzeContentTokens(tokenNames, document, isContent) {
  const content = [...new Set(tokenNames)].filter(isContent);
  const resolved = content.filter((name) => resolveContentToken(document, name).found);
  const used = new Set(content);
  const leaves = listLeaves(document || {});
  return {
    contentTokens: content.length,
    resolved: resolved.length,
    missing: content.filter((name) => !resolved.includes(name)),
    unusedLeaves: leaves.filter((leaf) => !leaf.token || (!used.has(leaf.token) && !used.has(`content.${leaf.token}`))).map((leaf) => leaf.path),
    untokenizableKeys: leaves.filter((leaf) => !leaf.token).map((leaf) => leaf.path),
    leafCount: leaves.length,
  };
}
